import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import CreateFOProductForm from '../components/CreateFOProductForm';
import EditCategoryModal from '../components/EditCategoryModal';
import CreateEditSupplierModal from '../components/CreateEditSupplierModal';
import axios from 'axios';
import { FiPlus, FiEye, FiEdit2, FiTrash2, FiSearch, FiX } from 'react-icons/fi';

const FOProductPage = () => {
    const [activeTab, setActiveTab] = useState('categories'); // 'categories', 'brands', or 'suppliers'
    
    // Categories state
    const [categories, setCategories] = useState([]);
    const [categoryLoading, setCategoryLoading] = useState(true);
    const [categoryError, setCategoryError] = useState('');
    const [showCreateCategoryForm, setShowCreateCategoryForm] = useState(false);
    const [categoryCurrentPage, setCategoryCurrentPage] = useState(1);
    const [categoryTotalPages, setCategoryTotalPages] = useState(1);
    const [categoryTotal, setCategoryTotal] = useState(0);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [showCategoryViewModal, setShowCategoryViewModal] = useState(false);
    const [showCategoryEditModal, setShowCategoryEditModal] = useState(false);
    const [showCategoryDeleteConfirm, setShowCategoryDeleteConfirm] = useState(false);
    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    const [categorySearchInput, setCategorySearchInput] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [categoryNames, setCategoryNames] = useState([]);

    // Brands state
    const [brands, setBrands] = useState([]);
    const [brandLoading, setBrandLoading] = useState(true);
    const [brandError, setBrandError] = useState('');
    const [showCreateBrandForm, setShowCreateBrandForm] = useState(false);
    const [brandCurrentPage, setBrandCurrentPage] = useState(1);
    const [brandTotalPages, setBrandTotalPages] = useState(1);
    const [brandTotal, setBrandTotal] = useState(0);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [showBrandViewModal, setShowBrandViewModal] = useState(false);
    const [showBrandEditModal, setShowBrandEditModal] = useState(false);
    const [showBrandDeleteConfirm, setShowBrandDeleteConfirm] = useState(false);
    const [brandSearchTerm, setBrandSearchTerm] = useState('');
    const [brandSearchInput, setBrandSearchInput] = useState('');
    const [brandFormData, setBrandFormData] = useState({
        Brand: ''
    });

    // Suppliers state
    const [suppliers, setSuppliers] = useState([]);
    const [supplierLoading, setSupplierLoading] = useState(true);
    const [supplierError, setSupplierError] = useState('');
    const [showCreateSupplierForm, setShowCreateSupplierForm] = useState(false);
    const [supplierCurrentPage, setSupplierCurrentPage] = useState(1);
    const [supplierTotalPages, setSupplierTotalPages] = useState(1);
    const [supplierTotal, setSupplierTotal] = useState(0);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [showSupplierViewModal, setShowSupplierViewModal] = useState(false);
    const [showSupplierEditModal, setShowSupplierEditModal] = useState(false);
    const [showSupplierDeleteConfirm, setShowSupplierDeleteConfirm] = useState(false);
    const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
    const [supplierSearchInput, setSupplierSearchInput] = useState('');

    // Fetch category names for filter
    useEffect(() => {
        const fetchCategoryNames = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/foproducts/categories/names`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (response.data.success) {
                    setCategoryNames(response.data.data);
                }
            } catch (err) {
                console.error('Failed to fetch category names:', err);
            }
        };
        fetchCategoryNames();
    }, []);

    // Fetch brands and suppliers totals on mount so tab labels show correct counts before clicking
    useEffect(() => {
        fetchBrands(1, '');
        fetchSuppliers(1, '');
    }, []);

    // Fetch categories (with order stats for Category tab: totalOrderItems, totalProfit, sorted by totalOrderItems)
    const fetchCategories = async (page, search = '', category = 'all') => {
        try {
            setCategoryLoading(true);
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/foproducts/categories?page=${page}&limit=20&withOrderStats=1`;
            
            if (search) apiUrl += `&search=${search}`;
            if (category && category !== 'all') apiUrl += `&category=${category}`;
            
            const token = localStorage.getItem('token');
            const response = await axios.get(
                apiUrl,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                setCategories(response.data.data);
                setCategoryTotalPages(response.data.pagination.pages);
                setCategoryTotal(response.data.pagination.total);
            }
        } catch (err) {
            setCategoryError(`Failed to fetch categories: ${err.response?.data?.error || err.message}`);
        } finally {
            setCategoryLoading(false);
        }
    };

    // Fetch brands (with order stats: totalOrderItems, sorted by totalOrderItems)
    const fetchBrands = async (page, search = '') => {
        try {
            setBrandLoading(true);
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/foproducts/brands?page=${page}&limit=20&withOrderStats=1`;
            
            if (search) apiUrl += `&search=${search}`;
            
            const token = localStorage.getItem('token');
            const response = await axios.get(
                apiUrl,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                setBrands(response.data.data);
                setBrandTotalPages(response.data.pagination.pages);
                setBrandTotal(response.data.pagination.total);
            }
        } catch (err) {
            setBrandError(`Failed to fetch brands: ${err.response?.data?.error || err.message}`);
        } finally {
            setBrandLoading(false);
        }
    };

    // Fetch suppliers (with order stats: totalOrderItems, totalCostSGD, sorted by totalOrderItems)
    const fetchSuppliers = async (page, search = '') => {
        try {
            setSupplierLoading(true);
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/foproducts/suppliers?page=${page}&limit=20&withOrderStats=1`;
            
            if (search) apiUrl += `&search=${search}`;
            
            const token = localStorage.getItem('token');
            const response = await axios.get(
                apiUrl,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                setSuppliers(response.data.data);
                setSupplierTotalPages(response.data.pagination.pages);
                setSupplierTotal(response.data.pagination.total);
            }
        } catch (err) {
            setSupplierError(`Failed to fetch suppliers: ${err.response?.data?.error || err.message}`);
        } finally {
            setSupplierLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'categories') {
            fetchCategories(categoryCurrentPage, categorySearchTerm, categoryFilter);
        } else if (activeTab === 'brands') {
            fetchBrands(brandCurrentPage, brandSearchTerm);
        } else if (activeTab === 'suppliers') {
            fetchSuppliers(supplierCurrentPage, supplierSearchTerm);
        }
    }, [activeTab, categoryCurrentPage, categorySearchTerm, categoryFilter, brandCurrentPage, brandSearchTerm, supplierCurrentPage, supplierSearchTerm]);

    // Category handlers
    const handleCreateCategorySuccess = (newCategory) => {
        setShowCreateCategoryForm(false);
        fetchCategories(categoryCurrentPage, categorySearchTerm, categoryFilter);
        // Refresh category names
        const fetchCategoryNames = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/foproducts/categories/names`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (response.data.success) {
                    setCategoryNames(response.data.data);
                }
            } catch (err) {
                console.error('Failed to fetch category names:', err);
            }
        };
        fetchCategoryNames();
    };

    const handleCategoryEditSuccess = () => {
        setShowCategoryEditModal(false);
        setSelectedCategory(null);
        fetchCategories(categoryCurrentPage, categorySearchTerm, categoryFilter);
    };

    const handleCategoryDelete = (category) => {
        setSelectedCategory(category);
        setShowCategoryDeleteConfirm(true);
    };

    const confirmCategoryDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/foproducts/categories/${selectedCategory._id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            setShowCategoryDeleteConfirm(false);
            setSelectedCategory(null);
            fetchCategories(categoryCurrentPage, categorySearchTerm, categoryFilter);
        } catch (err) {
            setCategoryError(`Failed to delete category: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleCategoryView = (category) => {
        setSelectedCategory(category);
        setShowCategoryViewModal(true);
    };

    const handleCategoryEdit = (category) => {
        setSelectedCategory(category);
        setShowCategoryEditModal(true);
    };

    const handleCategorySearch = () => {
        setCategorySearchTerm(categorySearchInput);
        setCategoryCurrentPage(1);
    };

    const handleCategoryClear = () => {
        setCategorySearchInput('');
        setCategorySearchTerm('');
        setCategoryFilter('all');
        setCategoryCurrentPage(1);
    };

    // Brand handlers
    const handleCreateBrandSuccess = (newBrand) => {
        setShowCreateBrandForm(false);
        fetchBrands(brandCurrentPage, brandSearchTerm);
    };

    const handleBrandEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const cleanedData = {
                Brand: brandFormData.Brand.trim()
            };

            const token = localStorage.getItem('token');
            await axios.put(
                `${process.env.REACT_APP_API_URL}/api/foproducts/brands/${selectedBrand._id}`,
                cleanedData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            setShowBrandEditModal(false);
            setSelectedBrand(null);
            setBrandFormData({ Brand: '' });
            fetchBrands(brandCurrentPage, brandSearchTerm);
        } catch (err) {
            setBrandError(`Failed to update brand: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleBrandDelete = (brand) => {
        setSelectedBrand(brand);
        setShowBrandDeleteConfirm(true);
    };

    const confirmBrandDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/foproducts/brands/${selectedBrand._id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            setShowBrandDeleteConfirm(false);
            setSelectedBrand(null);
            fetchBrands(brandCurrentPage, brandSearchTerm);
        } catch (err) {
            setBrandError(`Failed to delete brand: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleBrandView = (brand) => {
        setSelectedBrand(brand);
        setShowBrandViewModal(true);
    };

    const handleBrandEdit = (brand) => {
        setSelectedBrand(brand);
        setBrandFormData({
            Brand: brand.Brand || ''
        });
        setShowBrandEditModal(true);
    };

    const handleBrandSearch = () => {
        setBrandSearchTerm(brandSearchInput);
        setBrandCurrentPage(1);
    };

    const handleBrandClear = () => {
        setBrandSearchInput('');
        setBrandSearchTerm('');
        setBrandCurrentPage(1);
    };

    // Supplier handlers
    const handleCreateSupplierSuccess = (newSupplier) => {
        setShowCreateSupplierForm(false);
        fetchSuppliers(supplierCurrentPage, supplierSearchTerm);
    };

    const handleSupplierEditSuccess = () => {
        setShowSupplierEditModal(false);
        setSelectedSupplier(null);
        fetchSuppliers(supplierCurrentPage, supplierSearchTerm);
    };

    const handleSupplierDelete = (supplier) => {
        setSelectedSupplier(supplier);
        setShowSupplierDeleteConfirm(true);
    };

    const confirmSupplierDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/foproducts/suppliers/${selectedSupplier._id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            setShowSupplierDeleteConfirm(false);
            setSelectedSupplier(null);
            fetchSuppliers(supplierCurrentPage, supplierSearchTerm);
        } catch (err) {
            setSupplierError(`Failed to delete supplier: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleSupplierView = (supplier) => {
        setSelectedSupplier(supplier);
        setShowSupplierViewModal(true);
    };

    const handleSupplierEdit = (supplier) => {
        setSelectedSupplier(supplier);
        setShowSupplierEditModal(true);
    };

    const handleSupplierSearch = () => {
        setSupplierSearchTerm(supplierSearchInput);
        setSupplierCurrentPage(1);
    };

    const handleSupplierClear = () => {
        setSupplierSearchInput('');
        setSupplierSearchTerm('');
        setSupplierCurrentPage(1);
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                {/* Tabs */}
                <div className="mb-6 border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('categories')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'categories'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Categories ({categoryTotal})
                        </button>
                        <button
                            onClick={() => setActiveTab('brands')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'brands'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Brands ({brandTotal})
                        </button>
                        <button
                            onClick={() => setActiveTab('suppliers')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'suppliers'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Suppliers ({supplierTotal})
                        </button>
                    </nav>
                </div>

                {/* Categories Tab */}
                {activeTab === 'categories' && (
                    <>
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h1 className="text-2xl font-bold">GZ Categories</h1>
                                <p className="text-gray-600">Total: {categoryTotal} categories</p>
                            </div>
                            <button
                                onClick={() => setShowCreateCategoryForm(true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                            >
                                <FiPlus className="mr-2" />
                                Create New Category
                            </button>
                        </div>

                        {/* Search and Filter Bar */}
                        <div className="mb-6">
                            <div className="flex gap-2 mb-2">
                                <div className="relative flex-1">
                                    <input
                                        type="text"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                                        placeholder="Search by Category or Subcategory..."
                                        value={categorySearchInput}
                                        onChange={(e) => setCategorySearchInput(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleCategorySearch()}
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <FiSearch className="h-5 w-5 text-gray-400" />
                                    </div>
                                </div>
                                <select
                                    value={categoryFilter}
                                    onChange={(e) => {
                                        setCategoryFilter(e.target.value);
                                        setCategoryCurrentPage(1);
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="all">All Categories</option>
                                    {categoryNames.map((cat) => (
                                        <option key={cat} value={cat}>
                                            {cat}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    onClick={handleCategorySearch}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                                >
                                    <FiSearch className="mr-2" />
                                    Search
                                </button>
                                <button
                                    onClick={handleCategoryClear}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>

                        {categoryError && (
                            <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                                {categoryError}
                            </div>
                        )}

                        {/* Create Category Modal (EditCategoryModal with mode="create") */}
                        {showCreateCategoryForm && (
                            <EditCategoryModal
                                mode="create"
                                onSuccess={handleCreateCategorySuccess}
                                onCancel={() => setShowCreateCategoryForm(false)}
                            />
                        )}

                        {/* Edit Category Modal (component) */}
                        {showCategoryEditModal && selectedCategory && (
                            <EditCategoryModal
                                initialCategory={selectedCategory}
                                onSuccess={handleCategoryEditSuccess}
                                onCancel={() => {
                                    setShowCategoryEditModal(false);
                                    setSelectedCategory(null);
                                }}
                            />
                        )}

                        {/* View Category Modal */}
                        {showCategoryViewModal && selectedCategory && (
                            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                                <div className="relative top-20 mx-auto p-5 border max-w-2xl w-full shadow-lg rounded-md bg-white">
                                    <div className="flex justify-between items-center border-b pb-4 mb-4">
                                        <h3 className="text-xl font-medium">
                                            Category Details
                                        </h3>
                                        <button 
                                            onClick={() => { 
                                                setShowCategoryViewModal(false); 
                                                setSelectedCategory(null);
                                            }} 
                                            className="text-gray-500 hover:text-gray-700"
                                        >
                                            <FiX className="w-6 h-6" />
                                        </button>
                                    </div>
                                    <div className="space-y-6">
                                        <section>
                                            <h4 className="text-lg font-medium mb-3">Category Information</h4>
                                            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                                                <div>
                                                    <span className="text-gray-600 text-sm">Category:</span>
                                                    <span className="ml-2 text-blue-700 font-semibold">{selectedCategory.category}</span>
                                                </div>
                                                {selectedCategory.subcategory && selectedCategory.subcategory.length > 0 && (
                                                    <div>
                                                        <span className="text-gray-600 text-sm">Subcategories:</span>
                                                        <div className="mt-1 space-y-1">
                                                            {selectedCategory.subcategory.map((subcat, index) => (
                                                                <div key={index} className="text-blue-700">{index + 1}. {subcat}</div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="grid grid-cols-2 gap-4 mt-4">
                                                    <div>
                                                        <span className="text-gray-600 text-sm">Total Order Items:</span>
                                                        <span className="ml-2 text-blue-700 font-semibold">{selectedCategory.totalOrderItems != null ? Number(selectedCategory.totalOrderItems).toLocaleString() : '0'}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600 text-sm">Total Profit (SGD):</span>
                                                        <span className="ml-2 text-blue-700 font-semibold">{selectedCategory.totalProfit != null ? `SGD ${Number(selectedCategory.totalProfit).toFixed(2)}` : 'SGD 0.00'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Delete Category Confirmation Modal */}
                        {showCategoryDeleteConfirm && selectedCategory && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                                    <h2 className="text-xl font-bold mb-4">Confirm Delete</h2>
                                    <p className="mb-6">Are you sure you want to delete "{selectedCategory.category}{selectedCategory.subcategory && selectedCategory.subcategory.length > 0 ? ` - ${selectedCategory.subcategory.join(', ')}` : ''}"?</p>
                                    <div className="flex justify-end gap-2">
                                        <button
                                            onClick={() => { setShowCategoryDeleteConfirm(false); setSelectedCategory(null); }}
                                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={confirmCategoryDelete}
                                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Categories Table — below header, sorted by Total Order Items */}
                        <div className="bg-white rounded-lg shadow overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200" style={{ tableLayout: 'fixed' }}>
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '15%' }}>
                                            Category
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '60%' }}>
                                            Subcategory
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%' }}>
                                            Total Order Items
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%' }}>
                                            Total Profit
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '5%' }}>
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {categoryLoading ? (
                                        <tr>
                                            <td colSpan="5" className="px-4 py-4 text-center">
                                                Loading...
                                            </td>
                                        </tr>
                                    ) : categories.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="px-4 py-4 text-center">
                                                No categories found
                                            </td>
                                        </tr>
                                    ) : (
                                        categories.map((category) => (
                                            <tr key={category._id} className="hover:bg-gray-50">
                                                <td className="px-4 py-4 text-sm font-medium align-top" style={{ width: '15%' }}>
                                                    {category.category}
                                                </td>
                                                <td className="px-4 py-4 text-sm align-top break-words" style={{ width: '60%' }}>
                                                    <span className="inline-block max-w-full break-words whitespace-normal">
                                                        {category.subcategory && category.subcategory.length > 0
                                                            ? category.subcategory.join(', ')
                                                            : 'N/A'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4 text-sm align-top whitespace-nowrap" style={{ width: '10%' }}>
                                                    {category.totalOrderItems != null ? Number(category.totalOrderItems).toLocaleString() : '0'}
                                                </td>
                                                <td className="px-4 py-4 text-sm align-top whitespace-nowrap" style={{ width: '10%' }}>
                                                    {category.totalProfit != null ? `SGD ${Number(category.totalProfit).toFixed(2)}` : 'SGD 0.00'}
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2 align-top">
                                                    <button
                                                        onClick={() => handleCategoryView(category)}
                                                        className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center"
                                                        title="View Details"
                                                    >
                                                        <FiEye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleCategoryEdit(category)}
                                                        className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center"
                                                        title="Edit Category"
                                                    >
                                                        <FiEdit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleCategoryDelete(category)}
                                                        className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center"
                                                        title="Delete Category"
                                                    >
                                                        <FiTrash2 className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <Pagination 
                            currentPage={categoryCurrentPage}
                            totalPages={categoryTotalPages}
                            onPageChange={setCategoryCurrentPage}
                        />
                    </>
                )}

                {/* Brands Tab */}
                {activeTab === 'brands' && (
                    <>
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h1 className="text-2xl font-bold">GZ Brands</h1>
                                <p className="text-gray-600">Total: {brandTotal} brands</p>
                            </div>
                            <button
                                onClick={() => setShowCreateBrandForm(true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                            >
                                <FiPlus className="mr-2" />
                                Create New Brand
                            </button>
                        </div>

                        {/* Search Bar */}
                        <div className="mb-6">
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <input
                                        type="text"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                                        placeholder="Search by Brand..."
                                        value={brandSearchInput}
                                        onChange={(e) => setBrandSearchInput(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleBrandSearch()}
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <FiSearch className="h-5 w-5 text-gray-400" />
                                    </div>
                                </div>
                                <button
                                    onClick={handleBrandSearch}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                                >
                                    <FiSearch className="mr-2" />
                                    Search
                                </button>
                                <button
                                    onClick={handleBrandClear}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>

                        {brandError && (
                            <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                                {brandError}
                            </div>
                        )}

                        {/* Create Brand Form Modal */}
                        {showCreateBrandForm && (
                            <CreateFOProductForm
                                type="brand"
                                onSuccess={handleCreateBrandSuccess}
                                onCancel={() => setShowCreateBrandForm(false)}
                            />
                        )}

                        {/* Edit Brand Form Modal */}
                        {showBrandEditModal && selectedBrand && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
                                <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8">
                                    <h2 className="text-xl font-bold mb-4">Edit Brand</h2>
                                    <form onSubmit={handleBrandEditSubmit}>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Brand *
                                                </label>
                                                <input
                                                    type="text"
                                                    required
                                                    value={brandFormData.Brand}
                                                    onChange={(e) => setBrandFormData({ ...brandFormData, Brand: e.target.value })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                    placeholder="Brand name"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex justify-end gap-2 mt-6">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowBrandEditModal(false);
                                                    setSelectedBrand(null);
                                                    setBrandFormData({ Brand: '' });
                                                }}
                                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                            >
                                                Update
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}

                        {/* View Brand Modal */}
                        {showBrandViewModal && selectedBrand && (
                            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                                <div className="relative top-20 mx-auto p-5 border max-w-2xl w-full shadow-lg rounded-md bg-white">
                                    <div className="flex justify-between items-center border-b pb-4 mb-4">
                                        <h3 className="text-xl font-medium">
                                            Brand Details - <span className="ml-2 text-blue-700">{selectedBrand.Brand}</span>
                                        </h3>
                                        <button 
                                            onClick={() => { 
                                                setShowBrandViewModal(false); 
                                                setSelectedBrand(null);
                                            }} 
                                            className="text-gray-500 hover:text-gray-700"
                                        >
                                            <FiX className="w-6 h-6" />
                                        </button>
                                    </div>
                                    <div className="space-y-6">
                                        <section>
                                            <h4 className="text-lg font-medium mb-3">Brand Information</h4>
                                            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                                                <div>
                                                    <span className="text-gray-600 text-sm">Brand:</span>
                                                    <span className="ml-2 text-blue-700 font-semibold">{selectedBrand.Brand}</span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-4 mt-4">
                                                    <div>
                                                        <span className="text-gray-600 text-sm">Total Order Items:</span>
                                                        <span className="ml-2 text-blue-700 font-semibold">{selectedBrand.totalOrderItems != null ? Number(selectedBrand.totalOrderItems).toLocaleString() : '0'}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600 text-sm">Total Profit (SGD):</span>
                                                        <span className="ml-2 text-blue-700 font-semibold">{selectedBrand.totalProfitSGD != null ? `SGD ${Number(selectedBrand.totalProfitSGD).toFixed(2)}` : 'SGD 0.00'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Delete Brand Confirmation Modal */}
                        {showBrandDeleteConfirm && selectedBrand && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                                    <h2 className="text-xl font-bold mb-4">Confirm Delete</h2>
                                    <p className="mb-6">Are you sure you want to delete "{selectedBrand.Brand}"?</p>
                                    <div className="flex justify-end gap-2">
                                        <button
                                            onClick={() => { setShowBrandDeleteConfirm(false); setSelectedBrand(null); }}
                                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={confirmBrandDelete}
                                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Brands Table — sorted by Total Order Items */}
                        <div className="bg-white rounded-lg shadow overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Brand
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Total Order Items
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Total Profit (SGD)
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {brandLoading ? (
                                        <tr>
                                            <td colSpan="4" className="px-4 py-4 text-center">
                                                Loading...
                                            </td>
                                        </tr>
                                    ) : brands.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-4 py-4 text-center">
                                                No brands found
                                            </td>
                                        </tr>
                                    ) : (
                                        brands.map((brand) => (
                                            <tr key={brand._id} className="hover:bg-gray-50">
                                                <td className="px-4 py-4 text-sm font-medium">
                                                    {brand.Brand}
                                                </td>
                                                <td className="px-4 py-4 text-sm whitespace-nowrap">
                                                    {brand.totalOrderItems != null ? Number(brand.totalOrderItems).toLocaleString() : '0'}
                                                </td>
                                                <td className="px-4 py-4 text-sm whitespace-nowrap">
                                                    {brand.totalProfitSGD != null ? `SGD ${Number(brand.totalProfitSGD).toFixed(2)}` : 'SGD 0.00'}
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                                    <button
                                                        onClick={() => handleBrandView(brand)}
                                                        className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center"
                                                        title="View Details"
                                                    >
                                                        <FiEye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleBrandEdit(brand)}
                                                        className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center"
                                                        title="Edit Brand"
                                                    >
                                                        <FiEdit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleBrandDelete(brand)}
                                                        className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center"
                                                        title="Delete Brand"
                                                    >
                                                        <FiTrash2 className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <Pagination 
                            currentPage={brandCurrentPage}
                            totalPages={brandTotalPages}
                            onPageChange={setBrandCurrentPage}
                        />
                    </>
                )}

                {/* Suppliers Tab */}
                {activeTab === 'suppliers' && (
                    <>
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h1 className="text-2xl font-bold">GZ Suppliers</h1>
                                <p className="text-gray-600">Total: {supplierTotal} suppliers</p>
                            </div>
                            <button
                                onClick={() => setShowCreateSupplierForm(true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                            >
                                <FiPlus className="mr-2" />
                                Create New Supplier
                            </button>
                        </div>

                        {/* Search Bar */}
                        <div className="mb-6">
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <input
                                        type="text"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                                        placeholder="Search by Supplier Code or Name..."
                                        value={supplierSearchInput}
                                        onChange={(e) => setSupplierSearchInput(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSupplierSearch()}
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <FiSearch className="h-5 w-5 text-gray-400" />
                                    </div>
                                </div>
                                <button
                                    onClick={handleSupplierSearch}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                                >
                                    <FiSearch className="mr-2" />
                                    Search
                                </button>
                                <button
                                    onClick={handleSupplierClear}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>

                        {supplierError && (
                            <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                                {supplierError}
                            </div>
                        )}

                        {/* Create Supplier Modal */}
                        {showCreateSupplierForm && (
                            <CreateEditSupplierModal
                                mode="create"
                                onSuccess={handleCreateSupplierSuccess}
                                onCancel={() => setShowCreateSupplierForm(false)}
                            />
                        )}

                        {/* Edit Supplier Modal */}
                        {showSupplierEditModal && selectedSupplier && (
                            <CreateEditSupplierModal
                                mode="edit"
                                initialSupplier={selectedSupplier}
                                onSuccess={handleSupplierEditSuccess}
                                onCancel={() => {
                                    setShowSupplierEditModal(false);
                                    setSelectedSupplier(null);
                                }}
                            />
                        )}

                        {/* View Supplier Modal */}
                        {showSupplierViewModal && selectedSupplier && (
                            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                                <div className="relative top-20 mx-auto p-5 border max-w-2xl w-full shadow-lg rounded-md bg-white">
                                    <div className="flex justify-between items-center border-b pb-4 mb-4">
                                        <h3 className="text-xl font-medium">
                                            Supplier Details - <span className="ml-2 text-blue-700">{selectedSupplier.supplierCode}</span>
                                        </h3>
                                        <button 
                                            onClick={() => { 
                                                setShowSupplierViewModal(false); 
                                                setSelectedSupplier(null);
                                            }} 
                                            className="text-gray-500 hover:text-gray-700"
                                        >
                                            <FiX className="w-6 h-6" />
                                        </button>
                                    </div>
                                    <div className="space-y-6">
                                        <section>
                                            <h4 className="text-lg font-medium mb-3">Supplier Information</h4>
                                            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                                                <div>
                                                    <span className="text-gray-600 text-sm">Supplier Code:</span>
                                                    <span className="ml-2 text-blue-700 font-semibold">{selectedSupplier.supplierCode}</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600 text-sm">Name:</span>
                                                    <span className="ml-2 text-blue-700">{selectedSupplier.supplierName || 'N/A'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600 text-sm">Category:</span>
                                                    <span className="ml-2 text-blue-700">
                                                        {selectedSupplier.category && selectedSupplier.category.length > 0
                                                            ? (selectedSupplier.category || []).map(c => typeof c === 'object' && c?.category ? c.category : c).join(', ')
                                                            : 'N/A'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600 text-sm">Subcategory:</span>
                                                    <span className="ml-2 text-blue-700">
                                                        {selectedSupplier.subcategory && selectedSupplier.subcategory.length > 0
                                                            ? (selectedSupplier.subcategory || []).join(', ')
                                                            : 'N/A'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600 text-sm">Brand:</span>
                                                    <span className="ml-2 text-blue-700">
                                                        {selectedSupplier.brands && selectedSupplier.brands.length > 0
                                                            ? (selectedSupplier.brands || []).map(b => typeof b === 'object' && b?.Brand ? b.Brand : b).filter(Boolean).join(', ')
                                                            : 'N/A'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600 text-sm">Total Order Items:</span>
                                                    <span className="ml-2 text-blue-700 font-semibold">{selectedSupplier.totalOrderItems != null ? Number(selectedSupplier.totalOrderItems).toLocaleString() : '0'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600 text-sm">Total Cost (SGD):</span>
                                                    <span className="ml-2 text-blue-700 font-semibold">{selectedSupplier.totalCostSGD != null ? `SGD ${Number(selectedSupplier.totalCostSGD).toFixed(2)}` : 'SGD 0.00'}</span>
                                                </div>
                                                {selectedSupplier.remarks != null && selectedSupplier.remarks !== '' && (
                                                    <div>
                                                        <span className="text-gray-600 text-sm">Remarks:</span>
                                                        <p className="mt-1 text-blue-700 text-sm whitespace-pre-line">{selectedSupplier.remarks}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </section>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Delete Supplier Confirmation Modal */}
                        {showSupplierDeleteConfirm && selectedSupplier && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                                    <h2 className="text-xl font-bold mb-4">Confirm Delete</h2>
                                    <p className="mb-6">Are you sure you want to delete "{selectedSupplier.supplierCode}"?</p>
                                    <div className="flex justify-end gap-2">
                                        <button
                                            onClick={() => { setShowSupplierDeleteConfirm(false); setSelectedSupplier(null); }}
                                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={confirmSupplierDelete}
                                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Suppliers Table — sorted by Total Order Items */}
                        <div className="bg-white rounded-lg shadow overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Supplier Code
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Category
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Brand
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Total Order Items
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Total Cost (SGD)
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {supplierLoading ? (
                                        <tr>
                                            <td colSpan="7" className="px-4 py-4 text-center">
                                                Loading...
                                            </td>
                                        </tr>
                                    ) : suppliers.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="px-4 py-4 text-center">
                                                No suppliers found
                                            </td>
                                        </tr>
                                    ) : (
                                        suppliers.map((supplier) => (
                                            <tr key={supplier._id} className="hover:bg-gray-50">
                                                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                                    {supplier.supplierCode}
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-sm">
                                                    {supplier.supplierName || 'N/A'}
                                                </td>
                                                <td className="px-4 py-4 text-sm break-words">
                                                    {Array.isArray(supplier.category) && supplier.category.length > 0
                                                        ? supplier.category.map(c => (c && typeof c === 'object' && c.category) ? c.category : c).filter(Boolean).join(', ')
                                                        : 'N/A'}
                                                </td>
                                                <td className="px-4 py-4 text-sm break-words">
                                                    {Array.isArray(supplier.brands) && supplier.brands.length > 0
                                                        ? supplier.brands.map(b => (b && typeof b === 'object' && b.Brand) ? b.Brand : b).filter(Boolean).join(', ')
                                                        : 'N/A'}
                                                </td>
                                                <td className="px-4 py-4 text-sm whitespace-nowrap">
                                                    {supplier.totalOrderItems != null ? Number(supplier.totalOrderItems).toLocaleString() : '0'}
                                                </td>
                                                <td className="px-4 py-4 text-sm whitespace-nowrap">
                                                    {supplier.totalCostSGD != null ? `SGD ${Number(supplier.totalCostSGD).toFixed(2)}` : 'SGD 0.00'}
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                                    <button
                                                        onClick={() => handleSupplierView(supplier)}
                                                        className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center"
                                                        title="View Details"
                                                    >
                                                        <FiEye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleSupplierEdit(supplier)}
                                                        className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center"
                                                        title="Edit Supplier"
                                                    >
                                                        <FiEdit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleSupplierDelete(supplier)}
                                                        className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center"
                                                        title="Delete Supplier"
                                                    >
                                                        <FiTrash2 className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <Pagination 
                            currentPage={supplierCurrentPage}
                            totalPages={supplierTotalPages}
                            onPageChange={setSupplierCurrentPage}
                        />
                    </>
                )}
            </div>
        </Layout>
    );
};

export default FOProductPage;
